import '@mantine/carousel/styles.css';

import { Head, Link, usePage } from '@inertiajs/react';
import { Carousel } from '@mantine/carousel';
import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  GridCol,
  Group,
  Image,
  Modal,
  Paper,
  rem,
  Space,
  Stack,
  Tabs,
  Text,
  Textarea,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure, useIntersection, useMediaQuery } from '@mantine/hooks';
import { IconTrash } from '@tabler/icons-react';
import djs from 'dayjs/esm/index.js';
import relativeTime from 'dayjs/plugin/relativeTime';
import React from 'react';

import { MoneyFormatter } from '@/components/MoneyFormatter.jsx';
import OfferProgress from '@/components/OfferProgress.jsx';
import useCountdown from '@/components/useCountdown.jsx';
import useInertiaForm from '@/components/useInertiaForm.jsx';
import { createSiteLayout } from '@/pages/site/layouts/SiteLayout.jsx';

import classes from './Offer.module.css';

djs.extend(relativeTime);

function Hero({ offer }) {
  const isNarrowHeight = useMediaQuery('(max-height: 800px)');

  return (
    <Box>
      <Container ta={'center'} py={'xl'}>
        <Title fw={400} fz={rem(isNarrowHeight ? 45 : 64)} c="gfdark.9" lts={-2}>
          {offer.artwork.name}
        </Title>
        <Text fw={300} fz={rem(24)} mt={0} c="gfdark.5" lts={-1}>
          {offer.artwork.artist.name}
        </Text>
      </Container>
    </Box>
  );
}

function InvestButton({ offer, size = 'lg', val, ...props }) {
  return (
    <Group p={'sm'} justify={'center'} {...props}>
      {offer.status_action === 'COMING SOON' && (
        <Button fullWidth size={size}>
          {offer.status_action}
        </Button>
      )}
      {offer.status_action === 'BUY' && (
        <Button fullWidth size={size} component={Link} href={route('site.invest', [offer.id, { val: val }])}>
          {offer.status_action}
        </Button>
      )}
      {offer.status_action === 'SOLD OUT!' && (
        <Button fullWidth size={size} variant={'outline'} color={'green'}>
          {offer.status_action}
        </Button>
      )}
    </Group>
  );
}

function HeroOffer({ offer }) {
  // const isNarrowHeight = useMediaQuery('(max-height: 800px)');
  // const imageHeight = isNarrowHeight ? 355 : 500;
  const imageHeight = 500;

  // Story modal
  const [storyOpened, storyHandlers] = useDisclosure(false);

  const slides = offer?.artwork?.images.map((file, index) => (
    <Carousel.Slide key={index}>
      <Image height={imageHeight} src={file.url} fit={'contain'} />
    </Carousel.Slide>
  ));

  const videoSlides = offer?.artwork?.videos.map((document, index) =>
    document.original_name.includes('.mp4') ? (
      <Carousel.Slide key={index}>
        {/*<Image height={imageHeight} src={document.url} fit={'contain'} />*/}
        <video width={'100%'} height={imageHeight} controls>
          <source src={document.url + '#t=0.001'} type="video/mp4" />
          Your browser does not support the videos.
        </video>
      </Carousel.Slide>
    ) : null,
  );

  return (
    <Box py={'md'} pb={'xl'} mb={'xl'} pt={0}>
      <Modal
        opened={storyOpened}
        onClose={storyHandlers.close}
        size={'auto'}
        centered
        transitionProps={{ transition: 'fade', duration: 200 }}
      >
        <video style={{ maxHeight: '80vh', maxWidth: '80vw' }} loop autoPlay playsInline controls>
          <source src={offer?.artwork?.artist?.videos[0]?.url + '#t=0.001'} type="video/mp4" />
          Your browser does not support the videos.
        </video>
      </Modal>

      <Grid align={'stretch'} justify="space-between" gutter={'xl'}>
        <GridCol span={{ md: 8 }}>
          <Carousel
            classNames={classes}
            slideSize={'100%'}
            loop
            height={imageHeight}
            className={'w-border'}
            withIndicators={true}
            withControls={false}
            slideGap={'md'}
          >
            {slides}
            {videoSlides}
          </Carousel>

          <OfferProgress offer={offer} ta={'left'} mt={'xl'} p={'xl'} className="w-border" />
        </GridCol>
        <GridCol span={'auto'}>
          <Box h={'100%'} ta={'left'} p={'xl'} className="w-border">
            <Stack justify={'space-between'} h={'100%'}>
              {/* Header */}
              <Box>
                {/* Artist Info */}
                <Group gap={'sm'}>
                  {offer?.artwork?.artist?.images?.length && (
                    <Box style={{ position: 'relative' }}>
                      <Avatar
                        src={offer?.artwork?.artist?.images[0]?.url}
                        className={offer?.artwork?.artist?.videos?.length ? 'border-gradient-rounded' : ''}
                        onClick={() => {
                          if (!offer?.artwork?.artist?.videos?.length) {
                            return;
                          }
                          storyHandlers.open();
                        }}
                      />
                    </Box>
                  )}
                  <Box>
                    <Text fz={rem(12)} fw={'bold'} c="gfdark.5">
                      ARTIST
                    </Text>
                    <Text fz={rem(16)} fw={'lighter'} c="gfdark.5">
                      {offer.artwork.artist.name}
                    </Text>
                  </Box>
                </Group>

                {/* Offer Info */}
                <Text fz={rem(20)} c="gfdark.9" mt={'lg'} lts={-1}>
                  {offer.artwork.name}
                </Text>
                <Text fz={rem(14)} fw={'lighter'} c="gfdark.5">
                  {offer.artwork.medium}
                </Text>
              </Box>

              {/* Offer Details */}
              <Box>
                {/* --- */}
                <Group bg={'gf'} p={'sm'} justify={'space-between'}>
                  <Text fz={rem(12)} fw={'bold'} c="white" truncate>
                    INITIAL SALES PRICE
                  </Text>
                  <Text fz={rem(20)} c="white" truncate>
                    <MoneyFormatter value={offer.price} prefix={offer.price_currency} />
                  </Text>
                </Group>
                {/* --- */}
                <Group p={'sm'} justify={'space-between'}>
                  <Text fz={rem(12)} fw={'bold'} c="gfdark.5" truncate>
                    TOTAL SHARES
                  </Text>
                  <Text fz={rem(20)} c="gfdark.5" truncate>
                    {offer.quantity}
                  </Text>
                </Group>
                {/* --- */}
                {offer.status === 'published' && (
                  <Group p={'sm'} justify={'space-between'}>
                    <Text fz={rem(12)} fw={'bold'} c="gfdark.5" truncate>
                      NUMBER OF SHARES AVAILABLE
                    </Text>
                    <Text fz={rem(20)} c="gfdark.5" truncate>
                      {offer.quantity_available}
                    </Text>
                  </Group>
                )}
                {/* --- */}
                <Group p={'sm'} justify={'space-between'}>
                  <Text fz={rem(12)} fw={'bold'} c="gfdark.5" truncate>
                    PRICE PER SHARE
                  </Text>
                  <Text fz={rem(20)} c="gfdark.5" truncate>
                    <MoneyFormatter value={offer.price_per_share} prefix={offer.price_currency} />
                  </Text>
                </Group>
                {/* --- */}
                {offer?.active_discount && offer.status_action !== 'SOLD OUT!' && (
                  <Stack bg={'gf'} p={'sm'} align={'center'} gap={3}>
                    <Text fz={rem(18)} fw={'bold'} c="white" truncate>
                      LIMITED TIME OFFER
                    </Text>
                    {offer.active_discount.type === 'Extra Shares' && (
                      <>
                        <Text fz={rem(14)} fw={'bold'} c="white" truncate>
                          YOU WILL GET {offer.active_discount.value}% EXTRA SHARES
                        </Text>
                        <Text tt={'uppercase'} fz={rem(16)} fw={'bold'} c="white" truncate>
                          <DiscountTimer activeDiscount={offer.active_discount} />
                        </Text>
                      </>
                    )}
                  </Stack>
                )}
                {/* --- */}
                <InvestButton offer={offer} />
              </Box>
            </Stack>
          </Box>
        </GridCol>
      </Grid>
    </Box>
  );
}

function DiscountTimer({ activeDiscount }) {
  const expireDate = activeDiscount.dates[1];

  const remainedTime = expireDate ? Math.max(djs(expireDate).diff(djs(), 'second'), 0) : 0;
  if (remainedTime < 60 * 5) {
    const timeLeft = useCountdown(remainedTime);
    return timeLeft + ' seconds left';
  }

  return djs(expireDate).fromNow(true) + ' left';
}

function Cataloguing({ offer }) {
  return (
    <>
      <Text c={'gfdark.9'} fz={rem(32)} lts={-1}>
        Cataloguing
      </Text>

      <Box
        mt={'lg'}
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '10px',
          alignItems: 'center',
        }}
      >
        <Text c={'gfdark.9'} tt={'uppercase'} fw={'bolder'} fz={rem(12)}>
          TITLE
        </Text>
        <Text pl={'lg'} c={'gfdark.5'} fw={'lighter'} fz={rem(16)}>
          {offer?.artwork?.name}
        </Text>

        <Text c={'gfdark.9'} tt={'uppercase'} fw={'bolder'} fz={rem(12)}>
          ARTIST
        </Text>
        <Text pl={'lg'} c={'gfdark.5'} fw={'lighter'} fz={rem(16)}>
          {offer?.artwork?.artist?.name}
        </Text>

        <Text c={'gfdark.9'} tt={'uppercase'} fw={'bolder'} fz={rem(12)}>
          MEDIUM
        </Text>
        <Text pl={'lg'} c={'gfdark.5'} fw={'lighter'} fz={rem(16)}>
          {offer?.artwork?.medium}
        </Text>

        <Text c={'gfdark.9'} tt={'uppercase'} fw={'bolder'} fz={rem(12)}>
          YEAR
        </Text>
        <Text pl={'lg'} c={'gfdark.5'} fw={'lighter'} fz={rem(16)}>
          {offer?.artwork?.year}
        </Text>

        {offer?.artwork?.cataloguing?.map((item, index) => (
          <React.Fragment key={index}>
            <Text c={'gfdark.9'} tt={'uppercase'} fw={'bolder'} fz={rem(12)}>
              {item.name}
            </Text>
            <Text pl={'lg'} c={'gfdark.5'} fw={'lighter'} fz={rem(16)}>
              {item.value}
            </Text>
          </React.Fragment>
        ))}
      </Box>
    </>
  );
}

export default function Offer({ offer }) {
  const refs = {
    documents: useIntersection({ threshold: 1 }),
    exhibition: useIntersection({ threshold: 1 }),
    provenance: useIntersection({ threshold: 1 }),
    cataloguing: useIntersection({ threshold: 1 }),
    about: useIntersection({ threshold: 1 }),
    rewards: useIntersection({ threshold: 1 }),
    comments: useIntersection({ threshold: 1 }),
  };

  // Get active tab based on intersection
  const activeTab = Object.entries(refs).find(([_, { entry }]) => entry?.isIntersecting)?.[0] || null;

  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const hasRewards = offer.artwork.rewards?.length > 0;

  return (
    <>
      <Head>
        <title>{offer.artwork.name}</title>
      </Head>
      <Container>
        <Hero offer={offer} />
        <HeroOffer offer={offer} />

        <Grid align={'stretch'} justify="space-between" gutter={'xl'}>
          <GridCol span={{ md: hasRewards ? 8 : 12 }}>
            <Tabs
              color="gf"
              value={activeTab}
              onChange={tab => {
                window.scrollTo({ top: refs[tab].entry.target.offsetTop - 100, behavior: 'smooth' });
              }}
            >
              {/* Tabs */}
              <Tabs.List
                grow
                style={{ position: !isMobile ? 'sticky' : 'relative', top: !isMobile ? 60 : 0, background: 'white' }}
              >
                <Tabs.Tab value="about">ABOUT</Tabs.Tab>
                {offer?.artwork?.cataloguing?.length ? <Tabs.Tab value="cataloguing">CATALOGUING</Tabs.Tab> : null}
                {offer?.artwork?.provenance ? <Tabs.Tab value="provenance">PROVENANCE</Tabs.Tab> : null}
                {offer?.artwork?.exibition ? <Tabs.Tab value="exhibition">EXHIBITION HISTORY</Tabs.Tab> : null}
                {hasRewards && isMobile ? <Tabs.Tab value="rewards">PACKAGES</Tabs.Tab> : null}
                {offer?.artwork?.documents?.length ? <Tabs.Tab value="documents">DOCUMENTS</Tabs.Tab> : null}
                <Tabs.Tab value="comments">COMMENTS</Tabs.Tab>
              </Tabs.List>

              {/* About */}
              <Box p={'lg'} ref={refs.about.ref}>
                <Text c={'gfdark.9'} fz={rem(32)} lts={-1}>
                  About
                </Text>
                <Text fz={rem(16)} c={'gfdark.9'} dangerouslySetInnerHTML={{ __html: offer.artwork.description || '-' }}></Text>
              </Box>

              {/* Cataloguing */}
              {!offer?.artwork?.cataloguing?.length ? null : (
                <Box p={'lg'} ref={refs.cataloguing.ref}>
                  <Cataloguing offer={offer} />
                </Box>
              )}

              {/* Provenance */}
              {!offer?.artwork?.provenance ? null : (
                <Box p={'lg'} ref={refs.provenance.ref}>
                  <Text c={'gfdark.9'} fz={rem(32)} lts={-1}>
                    Provenance
                  </Text>

                  <Text fz={rem(16)} c={'gfdark.9'} dangerouslySetInnerHTML={{ __html: offer.artwork.provenance }}></Text>
                </Box>
              )}

              {/* Exhibition */}
              {!offer?.artwork?.exibition ? null : (
                <Box p={'lg'} ref={refs.exhibition.ref}>
                  <Text c={'gfdark.9'} fz={rem(32)}>
                    Exhibition History
                  </Text>

                  <Text fz={rem(16)} c={'gfdark.9'} dangerouslySetInnerHTML={{ __html: offer.artwork.exibition }}></Text>
                </Box>
              )}

              {/* Rewards */}
              {hasRewards && isMobile ? (
                <Box p={'lg'} ref={refs.rewards.ref}>
                  <Rewards offer={offer} />
                </Box>
              ) : null}

              {/* Documents */}
              {!offer?.artwork?.documents?.length ? null : (
                <Box p={'lg'} ref={refs.documents.ref}>
                  <Text c={'gfdark.9'} fz={rem(32)} lts={-1}>
                    Documents
                  </Text>

                  <Stack gap={'lg'}>
                    {offer.artwork.documents.map((document, index) => (
                      <Group key={index} justify={'space-between'} align={'center'}>
                        <a target="_blank" href={document.url} rel="noreferrer">
                          <Text fz={rem(16)}>{document.original_name}</Text>
                        </a>
                      </Group>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Comments */}
              <Box p={'lg'} ref={refs.comments.ref}>
                <Text c={'gfdark.9'} fz={rem(32)} lts={-1}>
                  Comments
                </Text>
              </Box>

              <Comments />

              <Space h={'xl'} />
            </Tabs>
          </GridCol>

          {/* Rewards */}
          {hasRewards && !isMobile && (
            <GridCol span={'auto'}>
              <Rewards offer={offer} />
            </GridCol>
          )}
        </Grid>
      </Container>
    </>
  );
}

function Comments() {
  const comments = usePage().props.comments;
  const errors = usePage().props.errors;

  const form = useInertiaForm(
    {
      initialValues: {
        content: '',
        commentable_id: usePage().props.offer.id,
        commentable_type: 'offer',
      },
      validate: {
        content: value => (value ? null : 'This field is required'),
      },
    },
    errors,
    route('site.comment', usePage().props.offer.id),
    'Thank you for your comment! It will be visible after moderation.',
  );

  return (
    <Stack gap={'lg'}>
      <Stack align={'flex-start'} justify={'flex-start'} gap={'xs'}>
        <Textarea w={'100%'} placeholder={'Write your comment here...'} {...form.getInputProps('content')} />
        <Stack justify={'flex-end'} gap={0} c={'dimmed'}>
          <Text size="xs">Your comment will be visible to everyone.</Text>
          <Text size="xs">Please be respectful and follow our community guidelines.</Text>
        </Stack>
        <Button size="sm" onClick={form.handleSubmit}>
          Submit
        </Button>
      </Stack>

      {comments?.length === 0 && <Text size="lg">No comments yet. Be the first to comment!</Text>}

      {comments?.map((comment, index) => (
        <Paper key={index} withBorder shadow="0" p={'lg'} opacity={comment.is_approved ? 1 : 0.5}>
          <Group>
            <Avatar size="md">{comment.author_initials}</Avatar>
            <Stack gap={0}>
              <Text size="lg" lh={1}>
                {comment.author_name}
              </Text>
              <Text size="xs" c={'dimmed'}>
                {djs(comment.created_at).format('MMMM D, YYYY HH:mm:ss')}
              </Text>
            </Stack>
            {comment.is_author && (
              <ActionIcon
                component={Link}
                method={'post'}
                preserveScroll={true}
                href={route('site.comment.delete', { id: comment.id })}
                onClick={e => !confirm('Are you sure you want to delete?') && e.preventDefault()}
                size="md"
                color="red"
                variant="light"
              >
                <IconTrash style={{ width: '70%', height: '70%' }} stroke={1.5} />
              </ActionIcon>
            )}
          </Group>
          <Text mt={'xs'} pt={0}>
            {comment.content}
          </Text>
        </Paper>
      ))}
    </Stack>
  );
}

function Rewards({ offer, ...props }) {
  // const theme = useMantineTheme();
  // const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);

  return (
    <Box h={'100%'} ta={'left'} mb={'xl'} {...props}>
      <Text c={'gfdark.9'} ta={'left'} fz={rem(32)} lts={-1}>
        Available Packages
      </Text>
      {offer.artwork.rewards?.map((reward, index) => (
        <Box key={index} mt={'lg'} className="w-border" p={'xl'}>
          <Text fz={rem(20)} c={'gfdark.9'}>
            {reward.title}
          </Text>
          <Text fz={rem(12)} c={'gfdark.9'}>
            Minimum Investment: <MoneyFormatter value={reward.value} prefix={offer.price_currency} />
          </Text>
          <Text mt={'sm'} fz={rem(16)} c={'gfdark.9'}>
            {reward.desc}
          </Text>

          <InvestButton p={0} mt={'md'} size="md" offer={offer} val={reward.value} />
        </Box>
      ))}
    </Box>
  );
}

Offer.layout = page => createSiteLayout(page);
